import React from "react";
import { styled } from "@mui/material/styles";
import { TextField, type TextFieldProps } from "@mui/material";
import { NumericFormat as NumberFormat } from "react-number-format";
import { VALIDATION_LIMITS } from "@/config/constants";

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "var(--neutral-white)",
    color: (theme.vars || theme).palette.text.primary,
    borderRadius: "var(--radius-control)",
    height: "40px",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "1px solid var(--neutral-500)",
    },
    "& .MuiInputAdornment-root": {
      "& img": {
        filter: "none",
      },
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "var(--neutral-600)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: (theme.vars || theme).palette.primary.main,
      borderWidth: 2,
    },
    "&.Mui-disabled": {
      backgroundColor: (theme.vars || theme).palette.action.disabledBackground,
      "& .MuiInputAdornment-root": {
        "& img": {
          filter: "opacity(0.38)",
        },
      },
      "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
      },
    },
    "& input::placeholder": {
      color: (theme.vars || theme).palette.text.secondary,
      opacity: 1,
    },
  },
}));

// Custom number format component
type NumberFormatCustomProps = {
  onChange: (_event: { target: { value: string } }) => void;
  decimalScale?: number;
  maxValue?: number;
  value?: string | number;
  [key: string]: any;
};

const NumberFormatCustom = React.forwardRef<
  HTMLInputElement,
  NumberFormatCustomProps
>((props, ref) => {
  const {
    onChange,
    decimalScale,
    maxValue = VALIDATION_LIMITS.MAX_NUMBER_FIELD_DEFAULT,
    value,
    ...other
  } = props;

  return (
    <NumberFormat
      {...other}
      value={value}
      getInputRef={ref}
      onValueChange={(values) => {
        // Only trigger onChange if the value actually changed
        if (values.value !== value) {
          onChange({
            target: {
              value: values.value,
            },
          });
        }
      }}
      thousandSeparator
      valueIsNumericString
      decimalScale={decimalScale}
      allowNegative={false}
      isAllowed={(values) => {
        const { value, floatValue } = values;
        const numValue = Number(value);
        if (floatValue === 0) {
          // If the value is 0, don't allow it
          return false;
        }
        return (
          floatValue === undefined ||
          (decimalScale === 0
            ? Number.isInteger(numValue) && numValue <= maxValue
            : numValue <= maxValue)
        );
      }}
    />
  );
});

type RpNumberFieldProps = Omit<TextFieldProps, "variant" | "size"> & {
  decimalScale?: number;
  maxValue?: number;
  readOnly?: boolean;
  slotProps?: {
    input?: Record<string, any>;
    htmlInput?: Record<string, any>;
  };
};

const RpNumberField = ({
  value,
  onChange,
  disabled = false,
  error = false,
  helperText,
  placeholder = "0.00",
  decimalScale = 2,
  maxValue = VALIDATION_LIMITS.MAX_NUMBER_FIELD,
  readOnly = false,
  slotProps = {},
  ...rest
}: RpNumberFieldProps) => {
  return (
    <StyledTextField
      {...rest}
      variant="outlined"
      size="small"
      value={value}
      onChange={onChange}
      error={error}
      helperText={helperText}
      disabled={disabled}
      placeholder={placeholder}
      slotProps={
        {
          input: {
            inputComponent: NumberFormatCustom,
            prefix: "",
            suffix: "",
            readOnly,
            ...slotProps?.input,
          },
          htmlInput: {
            decimalScale,
            maxValue,
            value, // Pass value to NumberFormatCustom
            ...slotProps?.htmlInput,
          },
        } as unknown as TextFieldProps["slotProps"]
      }
    />
  );
};

export default RpNumberField;
